import { useLocale } from 'next-intl';
import { usePaletteStore } from '../model/palette.store';
import { palettesService } from '@/features/design/render/api/palettes';

/**
 * Hook that combines palette API calls with store state updates
 * Following FSD architecture - API calls belong in feature layer
 */
export function usePaletteOperations() {
  const store = usePaletteStore();
  const locale = useLocale();

  const fetchPalettes = async (): Promise<void> => {
    // Avoid duplicate fetches
    if (store.isInitialized || store.isLoading) return;

    usePaletteStore.setState((state) => {
      state.isLoading = true;
      state.error = null;
    });

    try {
      const palettes = await palettesService.getPalettes();

      usePaletteStore.setState((state) => {
        state.palettes = palettes;
        state.isInitialized = true;
        state.isLoading = false;
        state.locale = locale;
      });
    } catch (error) {
      usePaletteStore.setState((state) => {
        state.error =
          error instanceof Error ? error.message : 'Failed to fetch palettes';
        state.isLoading = false;
      });
    }
  };

  return {
    // API operations
    fetchPalettes,

    // Store state (pass through)
    palettes: store.palettes,
    isLoading: store.isLoading,
    error: store.error,
    isInitialized: store.isInitialized,

    // Computed values
    getPaletteById: store.getPaletteById,
    getPaletteColors: store.getPaletteColors,

    // Reset
    reset: store.reset,
  };
}
